/**
 * WorkshopsCalendarGrid — month/week schedule grid for Bonsai Bar workshops.
 *
 * The render core behind the Regional / Teacher overview calendars. Builds the
 * week rows (a whole month for Month view, one week for Week view) and hands
 * each day to a WorkshopsCalendarDayCell. Today is highlighted, out-of-month
 * days dimmed. Dark-theme aware via `--nb-*` tokens (render inside an element
 * carrying `.nb-dashboards`).
 *
 * Grid body only — no card chrome, view toggle, or List table. The full card is
 * `WorkshopsCalendar`, which wraps this. Re-typed off the design bundle to
 * consume Nebari's pre-joined `WorkshopChip` (ISO dates, UTC math; see
 * workshops-calendar-dates / BON-69).
 */
import type { ReactElement } from 'react';

import {
  addDaysUtc,
  chipDayKey,
  DOW_LABELS,
  startOfWeekUtc,
  utcDayKey,
  viewerDayUtc,
  DEFAULT_WEEK_STARTS_ON,
  type WeekStartsOn,
} from './workshops-calendar-dates';
import { WorkshopsCalendarDayCell } from './WorkshopsCalendarDayCell';
import { WorkshopsCalendarDowHeader } from './WorkshopsCalendarDowHeader';
import type { DayClickHandler, WorkshopChip, WorkshopClickHandler } from './workshopsCalendarTypes';

export interface WorkshopsCalendarGridProps {
  workshops: WorkshopChip[];
  /** 'Month' shows the anchor's whole month; 'Week' shows one week. */
  view: 'Month' | 'Week';
  /** Which month/week is shown. */
  anchor: Date;
  /** Override for the "today" highlight (mostly for tests). */
  today?: Date;
  /** 0 = Sunday … 6 = Saturday. Defaults to Sunday, like every other Nebari calendar. */
  weekStartsOn?: WeekStartsOn;
  onDayClick?: DayClickHandler;
  onWorkshopClick?: WorkshopClickHandler;
}

interface PlacedChip extends WorkshopChip {
  /** Venue-local calendar-day key (`YYYY-MM-DD`) the chip buckets into. */
  dayKey: string;
}

/**
 * Calendar-day key of an instant as the VIEWER would call it — the day on their
 * own clock. Flooring the instant in UTC instead put the today marker a day
 * ahead for anyone west of Greenwich during their evening (BON-386). Shares
 * `viewerDayUtc` with the card's anchor so marker and scaffold agree.
 */
function browserLocalDayKey(d: Date): string {
  return utcDayKey(viewerDayUtc(d));
}

/** First-day-of-week dates: a whole month for Month view, one week for Week. */
function buildWeekStarts(view: 'Month' | 'Week', anchor: Date, weekStartsOn: WeekStartsOn): Date[] {
  if (view === 'Week') return [startOfWeekUtc(anchor, weekStartsOn)];
  const first = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1));
  const last = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + 1, 0));
  const weekStarts: Date[] = [];
  let cursor = startOfWeekUtc(first, weekStartsOn);
  while (cursor <= last) {
    weekStarts.push(new Date(cursor));
    cursor = addDaysUtc(cursor, 7);
  }
  return weekStarts;
}

export function WorkshopsCalendarGrid({
  workshops,
  view,
  anchor,
  today,
  weekStartsOn = DEFAULT_WEEK_STARTS_ON,
  onDayClick,
  onWorkshopClick,
}: WorkshopsCalendarGridProps): ReactElement {
  const todayKey = browserLocalDayKey(today ?? new Date());
  const monthIdx = view === 'Month' ? anchor.getUTCMonth() : -1;

  // Bucket by the chip's VENUE-LOCAL day (BON-386), matched against each cell's
  // calendar-day key — so a workshop lands on the day it happens at the venue,
  // not the UTC instant's day (which slips an evening western workshop forward).
  const placed: PlacedChip[] = workshops.map((w) => ({ ...w, dayKey: chipDayKey(w) }));

  const weekStarts = buildWeekStarts(view, anchor, weekStartsOn);
  const dowHeader = Array.from({ length: 7 }, (_, i) => DOW_LABELS[(weekStartsOn + i) % 7]);

  return (
    <div
      data-component="WorkshopsCalendarGrid"
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <WorkshopsCalendarDowHeader labels={dowHeader} />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {weekStarts.map((wkStart, wi) => (
          <div
            key={wi}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              borderBottom: wi < weekStarts.length - 1 ? '1px solid var(--nb-border)' : 'none',
              // A caller with less room can shorten Month rows by setting
              // `--nb-cal-month-row` on an ancestor.
              minHeight: view === 'Week' ? 0 : 'var(--nb-cal-month-row, 140px)',
              flex: view === 'Week' ? 1 : '0 0 auto',
            }}
          >
            {[0, 1, 2, 3, 4, 5, 6].map((di) => {
              const d = addDaysUtc(wkStart, di);
              // One key space for chip placement AND for the today/past marker:
              // `YYYY-MM-DD` strings compare lexicographically as calendar days.
              const cellKey = utcDayKey(d);
              return (
                <WorkshopsCalendarDayCell
                  key={di}
                  date={d}
                  dayWorkshops={placed.filter((p) => p.dayKey === cellKey)}
                  view={view}
                  isToday={cellKey === todayKey}
                  isPast={cellKey < todayKey}
                  outOfMonth={view === 'Month' && d.getUTCMonth() !== monthIdx}
                  isFirstColumn={di === 0}
                  onDayClick={onDayClick}
                  onWorkshopClick={onWorkshopClick}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
